import { defineTheme } from '@astryxdesign/core/theme';
import { neutralTheme } from '@astryxdesign/theme-neutral';

const DEFAULT_ACCENT = '#4690FF';
const cache = new Map<string, ReturnType<typeof defineTheme>>();

function normalizeAccent(accent: string | null | undefined): string {
  return accent && /^#[0-9a-f]{6}$/i.test(accent) ? accent.toUpperCase() : DEFAULT_ACCENT;
}

function luminance(hex: string) {
  const n = Number.parseInt(hex.slice(1), 16);
  const channel = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel((n >> 16) & 255) + 0.7152 * channel((n >> 8) & 255) + 0.0722 * channel(n & 255);
}

export function contrastRatio(a: string, b: string) {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

/** Label colour on the accent: white when it reaches 4.5:1, otherwise black. */
export function onAccent(accent: string) {
  return contrastRatio(accent, '#FFFFFF') >= 4.5 ? '#FFFFFF' : '#000000';
}

/**
 * Runtime Astryx theme for one studio. The accent comes from the studio
 * configuration (database), so the theme is defined at runtime with
 * `defineTheme({ extends })` — the documented path for dynamic themes. The HCT
 * colour config derives on-accent, hover and muted tokens with contrast guarantees.
 * Surfaces are pinned to a deep navy-black background with light text (dark only).
 */
export function studioTheme(accent?: string | null) {
  const seed = normalizeAccent(accent);
  const cached = cache.get(seed);
  if (cached) return cached;
  const theme = defineTheme({
    name: `studio-${seed.slice(1).toLowerCase()}`,
    extends: neutralTheme,
    color: { accent: seed, neutralStyle: 'neutral', contrast: 'high' },
    typography: {
      scale: { base: 15, ratio: 1.2 },
      body: {
        family: 'Onest Variable',
        fallbacks: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, sans-serif',
      },
      heading: { weight: 'bold', weights: { 1: 'bold', 2: 'bold', 3: 'semibold' } },
    },
    radius: { base: 6, multiplier: 1.5 },
    motion: { fast: 150, medium: 320, slow: 800, ratio: 0.75 },
    tokens: {
      // The studio colour exactly as configured (the HCT scale above derives hover,
      // pressed and muted tints); the label on it is chosen for ≥ 4.5:1 contrast.
      '--color-accent': seed,
      '--color-on-accent': onAccent(seed),
      // Deep navy-black surfaces with glassy cards (sampled from the reference design).
      '--color-background-body': '#090D13',
      '--color-background-surface': '#0E131A',
      '--color-background-card': '#161C23',
      '--color-background-popover': '#121A23',
      '--color-background-muted': '#1B222A',
      '--color-text-primary': '#F3F6FA',
      '--color-text-secondary': '#8C95A3',
      '--color-border': 'rgba(255, 255, 255, 0.08)',
      '--color-border-emphasized': 'rgba(255, 255, 255, 0.18)',
      '--focus-outline-color': 'var(--color-accent)',
    },
    components: {
      // Day and time choices read as tappable chips; the chosen one takes the accent.
      'toggle-button': {
        base: {
          backgroundColor: 'var(--color-background-muted)',
          fontVariantNumeric: 'tabular-nums',
        },
        isPressed: {
          backgroundColor: 'var(--color-accent)',
          color: 'var(--color-on-accent)',
        },
      },
    },
  });
  cache.set(seed, theme);
  return theme;
}

export const neutralAppTheme = studioTheme(DEFAULT_ACCENT);
